'use strict';

var uint8arrayExtras = require('uint8array-extras');
var index = require('../xdr/index.js');
var keypair = require('./keypair.js');
var strkey = require('./strkey.js');
var hashing = require('./hashing.js');
var address = require('./address.js');
var scval = require('./scval.js');
var sorobanAddressCredentials = require('../xdr/generated/soroban-address-credentials.js');
var hashIdPreimageSorobanAuthorizationWithAddress = require('../xdr/generated/hash-id-preimage-soroban-authorization-with-address.js');
var hashIdPreimageSorobanAuthorization = require('../xdr/generated/hash-id-preimage-soroban-authorization.js');
var sorobanAddressCredentialsWithDelegates = require('../xdr/generated/soroban-address-credentials-with-delegates.js');
var sorobanDelegateSignature = require('../xdr/generated/soroban-delegate-signature.js');
var scVal = require('../xdr/generated/sc-val.js');
var sorobanAuthorizationEntry = require('../xdr/generated/soroban-authorization-entry.js');
var sorobanCredentials = require('../xdr/generated/soroban-credentials.js');
var hashIdPreimage = require('../xdr/generated/hash-id-preimage.js');

function toScVal(value) {
  if (scVal.ScVal.is(value)) return value;
  if (typeof value !== "object" || value === null) return null;
  const ctor = value.constructor;
  if (ctor?.schema?.name !== scVal.ScVal.schema.name) return null;
  const toXdrObject = value.toXdrObject;
  if (typeof toXdrObject !== "function") return null;
  try {
    return scVal.ScVal.fromXdr(scVal.ScVal.schema.encode(toXdrObject.call(value)));
  } catch {
    return null;
  }
}
async function authorizeEntry(entry, signer, validUntilLedgerSeq, networkPassphrase, forAddress) {
  if (entry.credentials.type === "sorobanCredentialsSourceAccount") {
    return entry;
  }
  const credentials = entry.credentials;
  const addrAuth = getAddressCredentials(credentials);
  if (addrAuth === null) {
    throw new Error(`unsupported credential type ${credentials.type}`);
  }
  const preimage = buildAuthorizationEntryPreimage(
    entry,
    validUntilLedgerSeq,
    networkPassphrase
  );
  const payload = hashing.hash(preimage.toXdr());
  let signatureScVal;
  let targetAddress = forAddress;
  let sigResult = null;
  if (typeof signer === "function") {
    sigResult = await signer(preimage, Uint8Array.from(payload));
  }
  if (sigResult !== null && typeof sigResult === "object" && "signatureScVal" in sigResult) {
    const candidate = sigResult.signatureScVal;
    const asScVal = toScVal(candidate);
    if (asScVal === null) {
      throw new TypeError(
        `signatureScVal must be an xdr.ScVal, got ${candidate === null ? "null" : typeof candidate}`
      );
    }
    signatureScVal = asScVal;
    targetAddress ??= sigResult.address;
  } else {
    let signature;
    let publicKey;
    if (typeof signer === "function") {
      if (sigResult !== null && typeof sigResult === "object" && "signature" in sigResult) {
        signature = sigResult.signature;
        publicKey = sigResult.publicKey;
      } else if (uint8arrayExtras.isUint8Array(sigResult)) {
        signature = sigResult;
        publicKey = address.Address.fromScAddress(addrAuth.address).toString();
      } else {
        throw new TypeError(
          `SigningCallback must resolve to a Uint8Array, { signature, publicKey }, or { signatureScVal }; got ${sigResult === null ? "null" : typeof sigResult}`
        );
      }
    } else {
      signature = signer.sign(payload);
      publicKey = signer.publicKey();
    }
    if (typeof publicKey !== "string") {
      throw new TypeError(
        `expected a public key string from the signer, got ${typeof publicKey}`
      );
    }
    if (!uint8arrayExtras.isUint8Array(signature)) {
      throw new TypeError(
        `expected a Uint8Array signature from the signer, got ${signature === null ? "null" : typeof signature}`
      );
    }
    if (!keypair.Keypair.fromPublicKey(publicKey).verify(payload, signature)) {
      throw new Error(`signature doesn't match payload`);
    }
    const sigScVal = scval.nativeToScVal(
      {
        public_key: strkey.StrKey.decodeEd25519PublicKey(publicKey),
        signature
      },
      {
        type: {
          public_key: ["symbol", null],
          signature: ["symbol", null]
        }
      }
    );
    signatureScVal = scVal.ScVal.scvVec([sigScVal]);
  }
  const { credentials: signedCredentials, matched } = applyExpirationAndSignature(
    credentials,
    validUntilLedgerSeq,
    signatureScVal,
    targetAddress
  );
  if (matched === 0) {
    throw new Error(
      `the authorization entry has no credential node for address ${targetAddress}`
    );
  }
  return new sorobanAuthorizationEntry.SorobanAuthorizationEntry({
    credentials: signedCredentials,
    rootInvocation: entry.rootInvocation
  });
}
function authorizeInvocation(params) {
  const {
    signer,
    validUntilLedgerSeq,
    invocation,
    networkPassphrase,
    publicKey = "",
    authV2 = true
  } = params;
  const kp = keypair.Keypair.random().rawPublicKey();
  const nonce = index.Int64(bytesToInt64(kp));
  const pk = publicKey || (signer instanceof keypair.Keypair ? signer.publicKey() : null);
  if (!pk) {
    throw new Error(`authorizeInvocation requires publicKey parameter`);
  }
  const addressCredentials = new sorobanAddressCredentials.SorobanAddressCredentials({
    address: new address.Address(pk).toScAddress(),
    nonce,
    signatureExpirationLedger: 0,
    // replaced
    signature: scVal.ScVal.scvVec([])
    // replaced
  });
  const entry = new sorobanAuthorizationEntry.SorobanAuthorizationEntry({
    rootInvocation: invocation,
    credentials: authV2 ? sorobanCredentials.SorobanCredentials.sorobanCredentialsAddressV2(addressCredentials) : sorobanCredentials.SorobanCredentials.sorobanCredentialsAddress(addressCredentials)
  });
  return authorizeEntry(entry, signer, validUntilLedgerSeq, networkPassphrase);
}
function buildAuthorizationEntryPreimage(entry, validUntilLedgerSeq, networkPassphrase) {
  const credentials = entry.credentials;
  const addrAuth = getAddressCredentials(credentials);
  if (addrAuth === null) {
    throw new Error(
      `cannot build a signature payload for credential type ${credentials.type}`
    );
  }
  const networkId = hashing.hash(networkPassphrase);
  switch (credentials.type) {
    // legacy address credentials are not address-bound
    case "sorobanCredentialsAddress":
      return hashIdPreimage.HashIdPreimage.envelopeTypeSorobanAuthorization(
        new hashIdPreimageSorobanAuthorization.HashIdPreimageSorobanAuthorization({
          networkId,
          nonce: addrAuth.nonce,
          invocation: entry.rootInvocation,
          signatureExpirationLedger: validUntilLedgerSeq
        })
      );
    // ADDRESS_V2 and ADDRESS_WITH_DELEGATES bind the address into the signed
    // payload via the WithAddress preimage (CAP-71)
    case "sorobanCredentialsAddressV2":
    case "sorobanCredentialsAddressWithDelegates":
      return hashIdPreimage.HashIdPreimage.envelopeTypeSorobanAuthorizationWithAddress(
        new hashIdPreimageSorobanAuthorizationWithAddress.HashIdPreimageSorobanAuthorizationWithAddress({
          networkId,
          nonce: addrAuth.nonce,
          invocation: entry.rootInvocation,
          address: addrAuth.address,
          signatureExpirationLedger: validUntilLedgerSeq
        })
      );
    default:
      throw new Error(`unsupported credential type ${credentials.type}`);
  }
}
function buildWithDelegatesEntry(params) {
  const { entry, validUntilLedgerSeq, delegates, signature } = params;
  const credentials = entry.credentials;
  const addrAuth = getAddressCredentials(credentials);
  if (addrAuth === null || credentials.type === "sorobanCredentialsAddressWithDelegates") {
    throw new Error(
      `buildWithDelegatesEntry expects ADDRESS or ADDRESS_V2 credentials, got ${credentials.type}`
    );
  }
  return new sorobanAuthorizationEntry.SorobanAuthorizationEntry({
    rootInvocation: entry.rootInvocation,
    credentials: sorobanCredentials.SorobanCredentials.sorobanCredentialsAddressWithDelegates(
      new sorobanAddressCredentialsWithDelegates.SorobanAddressCredentialsWithDelegates({
        addressCredentials: new sorobanAddressCredentials.SorobanAddressCredentials({
          address: addrAuth.address,
          nonce: addrAuth.nonce,
          signatureExpirationLedger: validUntilLedgerSeq,
          signature: signature ?? scVal.ScVal.scvVoid()
        }),
        delegates: buildDelegateNodes(delegates)
      })
    )
  });
}
function buildDelegateNodes(delegates) {
  const nodes = delegates.map(
    (delegate) => new sorobanDelegateSignature.SorobanDelegateSignature({
      address: new address.Address(delegate.address).toScAddress(),
      signature: delegate.signature ?? scVal.ScVal.scvVoid(),
      nestedDelegates: buildDelegateNodes(delegate.nestedDelegates ?? [])
    })
  );
  nodes.sort(
    (a, b) => uint8arrayExtras.compareUint8Arrays(a.address.toXdr(), b.address.toXdr())
  );
  for (let i = 1; i < nodes.length; i++) {
    if (uint8arrayExtras.compareUint8Arrays(
      nodes[i - 1].address.toXdr(),
      nodes[i].address.toXdr()
    ) === 0) {
      throw new Error(
        `duplicate delegate address ${address.Address.fromScAddress(
          nodes[i].address
        ).toString()}`
      );
    }
  }
  return nodes;
}
function getAddressCredentials(credentials) {
  switch (credentials.type) {
    case "sorobanCredentialsAddress":
      return credentials.address;
    case "sorobanCredentialsAddressV2":
      return credentials.addressV2;
    case "sorobanCredentialsAddressWithDelegates":
      return credentials.addressWithDelegates.addressCredentials;
    default:
      return null;
  }
}
function applyExpirationAndSignature(credentials, validUntilLedgerSeq, signature, forAddress) {
  const topAddr = getAddressCredentials(credentials);
  if (topAddr === null) {
    return { credentials, matched: 0 };
  }
  let matched = 0;
  const topIsTarget = forAddress === void 0 || address.Address.fromScAddress(topAddr.address).toString() === forAddress;
  if (topIsTarget) {
    matched++;
  }
  const newTopAddr = new sorobanAddressCredentials.SorobanAddressCredentials({
    address: topAddr.address,
    nonce: topAddr.nonce,
    signatureExpirationLedger: validUntilLedgerSeq,
    signature: topIsTarget ? signature : topAddr.signature
  });
  switch (credentials.type) {
    case "sorobanCredentialsAddress":
      return {
        credentials: sorobanCredentials.SorobanCredentials.sorobanCredentialsAddress(newTopAddr),
        matched
      };
    case "sorobanCredentialsAddressV2":
      return {
        credentials: sorobanCredentials.SorobanCredentials.sorobanCredentialsAddressV2(newTopAddr),
        matched
      };
    case "sorobanCredentialsAddressWithDelegates": {
      const withDelegates = credentials.addressWithDelegates;
      const newDelegates = forAddress === void 0 ? withDelegates.delegates : rebuildDelegatesWithSignature(
        withDelegates.delegates,
        forAddress,
        signature,
        () => {
          matched++;
        }
      );
      return {
        credentials: sorobanCredentials.SorobanCredentials.sorobanCredentialsAddressWithDelegates(
          new sorobanAddressCredentialsWithDelegates.SorobanAddressCredentialsWithDelegates({
            addressCredentials: newTopAddr,
            delegates: newDelegates
          })
        ),
        matched
      };
    }
    default:
      return { credentials, matched };
  }
}
function rebuildDelegatesWithSignature(delegates, forAddress, signature, onMatch) {
  return delegates.map((delegate) => {
    const isMatch = address.Address.fromScAddress(delegate.address).toString() === forAddress;
    if (isMatch) {
      onMatch();
    }
    return new sorobanDelegateSignature.SorobanDelegateSignature({
      address: delegate.address,
      signature: isMatch ? signature : delegate.signature,
      nestedDelegates: rebuildDelegatesWithSignature(
        delegate.nestedDelegates,
        forAddress,
        signature,
        onMatch
      )
    });
  });
}
function inspectAuthEntry(entry) {
  const credentials = entry.credentials;
  const addrAuth = getAddressCredentials(credentials);
  let credentialType;
  switch (credentials.type) {
    case "sorobanCredentialsSourceAccount":
      credentialType = "sourceAccount";
      break;
    case "sorobanCredentialsAddress":
      credentialType = "address";
      break;
    case "sorobanCredentialsAddressV2":
      credentialType = "addressV2";
      break;
    case "sorobanCredentialsAddressWithDelegates":
      credentialType = "addressWithDelegates";
      break;
    default:
      throw new Error(
        `unsupported credential type ${credentials.type}`
      );
  }
  const signers = collectSignatureNodes(credentials).map(
    (node) => ({
      address: address.Address.fromScAddress(node.address).toString(),
      signed: signaturePresent(node.signature),
      signatures: parseEd25519Signatures(node.signature),
      rawSignature: node.signature
    })
  );
  return {
    credentialType,
    address: addrAuth === null ? null : address.Address.fromScAddress(addrAuth.address).toString(),
    nonce: addrAuth === null ? null : addrAuth.nonce,
    signatureExpirationLedger: addrAuth === null ? null : addrAuth.signatureExpirationLedger,
    signers,
    signed: signers.length > 0 && signers.every((signer) => signer.signed),
    invocation: entry.rootInvocation
  };
}
function collectSignatureNodes(credentials) {
  const addrAuth = getAddressCredentials(credentials);
  if (addrAuth === null) {
    return [];
  }
  const nodes = [
    { address: addrAuth.address, signature: addrAuth.signature }
  ];
  if (credentials.type === "sorobanCredentialsAddressWithDelegates") {
    const walk = (delegates) => {
      delegates.forEach((delegate) => {
        nodes.push({
          address: delegate.address,
          signature: delegate.signature
        });
        walk(delegate.nestedDelegates);
      });
    };
    walk(credentials.addressWithDelegates.delegates);
  }
  return nodes;
}
function checkAuthEntryReadiness(entry, currentLedgerSeq) {
  if (!Number.isInteger(currentLedgerSeq) || currentLedgerSeq < 0 || currentLedgerSeq > 4294967295) {
    throw new Error(
      `currentLedgerSeq must be a uint32 ledger sequence, got ${currentLedgerSeq}`
    );
  }
  const info = inspectAuthEntry(entry);
  if (info.credentialType === "sourceAccount") {
    return { ready: true, expired: false, unsignedBy: [] };
  }
  const expired = currentLedgerSeq >= (info.signatureExpirationLedger ?? 0);
  const unsignedBy = info.signers.filter((signer) => !signer.signed).map((signer) => signer.address);
  return { ready: !expired && unsignedBy.length === 0, expired, unsignedBy };
}
function signaturePresent(signature) {
  switch (signature.type) {
    case "scvVoid":
      return false;
    case "scvVec":
      return (signature.value ?? []).length > 0;
    default:
      return true;
  }
}
function parseEd25519Signatures(signature) {
  if (signature.type !== "scvVec") {
    return null;
  }
  const parsed = [];
  for (const element of signature.value ?? []) {
    if (element.type !== "scvMap") {
      return null;
    }
    let publicKey = null;
    let sig = null;
    for (const mapEntry of element.value ?? []) {
      const { key, val } = mapEntry;
      if (key.type !== "scvSymbol" || val.type !== "scvBytes") {
        return null;
      }
      switch (key.value.toString()) {
        case "public_key":
          publicKey = val.value.value;
          break;
        case "signature":
          sig = val.value.value;
          break;
        default:
          return null;
      }
    }
    if (publicKey === null || sig === null || publicKey.length !== 32 || sig.length !== 64) {
      return null;
    }
    parsed.push({
      publicKey: strkey.StrKey.encodeEd25519PublicKey(publicKey),
      signature: sig
    });
  }
  return parsed;
}
function bytesToInt64(bytes) {
  const buf = bytes.subarray(0, 8);
  if (buf.length < 8) {
    throw new Error(
      `need at least 8 bytes to convert to Int64, got ${bytes.length}`
    );
  }
  const view = new DataView(buf.buffer, buf.byteOffset, 8);
  const value = view.getBigInt64(0, false);
  return value;
}

exports.authorizeEntry = authorizeEntry;
exports.authorizeInvocation = authorizeInvocation;
exports.buildAuthorizationEntryPreimage = buildAuthorizationEntryPreimage;
exports.buildWithDelegatesEntry = buildWithDelegatesEntry;
exports.checkAuthEntryReadiness = checkAuthEntryReadiness;
exports.getAddressCredentials = getAddressCredentials;
exports.inspectAuthEntry = inspectAuthEntry;
//# sourceMappingURL=auth.js.map
